import SEO from '@/components/SEO';

const CookiePolicy = () => {
  return (
    <>
      <SEO
        title="Cookie Policy - SeniorThrive"
        description="SeniorThrive's cookie policy explaining what cookies and tracking technologies we use, why we use them, and how you can control them."
        keywords="cookie policy, cookies, tracking, privacy, SeniorThrive"
        canonical="https://seniorthrive.com/cookies"
      />

      <div className="min-h-screen bg-gradient-subtle">
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-foreground mb-4">
                SeniorThrive Cookie Policy
              </h1>
              <p className="text-lg text-muted-foreground">
                Last Updated and Effective Date: April 20, 2026
              </p>
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              <div className="bg-card border border-border rounded-xl p-8 mb-8 shadow-card">
                <p className="text-muted-foreground leading-relaxed">
                  This Cookie Policy explains what cookies and similar tracking technologies SeniorThrive LLC ("SeniorThrive," "we," "us," or "our") uses on our website (seniorthrive.com) and web application, why we use them, and your choices regarding their use. This policy should be read together with our <a href="/privacy" className="text-primary hover:underline">Privacy Policy</a> and <a href="/terms" className="text-primary hover:underline">Terms of Service</a>.
                </p>
              </div>

              <div className="space-y-12">
                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">What Are Cookies?</h2>
                  <p className="text-muted-foreground">
                    Cookies are small text files that are placed on your device (computer, tablet, or phone) when you visit a website. They are widely used to make websites work more efficiently, provide a better user experience, and give website owners information about how visitors use their site. In addition to cookies, we may use similar technologies such as pixels, local storage, and session storage to collect and store information.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Types of Cookies We Use</h2>
                  <p className="text-muted-foreground mb-6">We categorize cookies into three tiers. You may control which tiers are active through our cookie banner or your browser settings.</p>

                  <div className="space-y-8">
                    <div className="bg-card border border-border rounded-xl p-6">
                      <h3 className="text-xl font-bold text-foreground mb-2">Tier 1: Strictly Necessary Cookies</h3>
                      <p className="text-muted-foreground mb-4">These cookies are essential for the website and application to function. They cannot be switched off because the Services would not work without them. They are usually set in response to actions you take, such as logging in or setting your privacy preferences.</p>

                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-border">
                          <thead>
                            <tr className="bg-accent">
                              <th className="border border-border p-3 text-left font-bold">Cookie / Technology</th>
                              <th className="border border-border p-3 text-left font-bold">Provider</th>
                              <th className="border border-border p-3 text-left font-bold">Purpose</th>
                              <th className="border border-border p-3 text-left font-bold">Duration</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="border border-border p-3 text-muted-foreground">seniorthrive.auth</td>
                              <td className="border border-border p-3 text-muted-foreground">Supabase</td>
                              <td className="border border-border p-3 text-muted-foreground">Authentication session token. Keeps you logged in and secures your account.</td>
                              <td className="border border-border p-3 text-muted-foreground">Session (auto-refreshed)</td>
                            </tr>
                            <tr>
                              <td className="border border-border p-3 text-muted-foreground">Cookie consent preferences</td>
                              <td className="border border-border p-3 text-muted-foreground">SeniorThrive</td>
                              <td className="border border-border p-3 text-muted-foreground">Remembers your cookie consent choices so we do not ask you again on every visit.</td>
                              <td className="border border-border p-3 text-muted-foreground">1 year</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="bg-card border border-border rounded-xl p-6">
                      <h3 className="text-xl font-bold text-foreground mb-2">Tier 2: Functional Cookies</h3>
                      <p className="text-muted-foreground mb-4">These cookies enable enhanced functionality and personalization, such as live chat support, accessibility tools, and error monitoring. If you do not allow these cookies, some features may not work as intended.</p>

                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-border">
                          <thead>
                            <tr className="bg-accent">
                              <th className="border border-border p-3 text-left font-bold">Cookie / Technology</th>
                              <th className="border border-border p-3 text-left font-bold">Provider</th>
                              <th className="border border-border p-3 text-left font-bold">Purpose</th>
                              <th className="border border-border p-3 text-left font-bold">Duration</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="border border-border p-3 text-muted-foreground">Intercom cookies</td>
                              <td className="border border-border p-3 text-muted-foreground">Intercom, Inc.</td>
                              <td className="border border-border p-3 text-muted-foreground">Powers our live chat and support messaging. Identifies your session so support conversations persist across pages. On the SaaS app, associates your account with support history.</td>
                              <td className="border border-border p-3 text-muted-foreground">Up to 1 year</td>
                            </tr>
                            <tr>
                              <td className="border border-border p-3 text-muted-foreground">Sentry session data</td>
                              <td className="border border-border p-3 text-muted-foreground">Sentry (Functional Software, Inc.)</td>
                              <td className="border border-border p-3 text-muted-foreground">Error monitoring and performance tracking in the SaaS application. Records anonymized session replays when errors occur to help us fix bugs. Does not collect personally identifiable information. Sensitive form inputs are masked.</td>
                              <td className="border border-border p-3 text-muted-foreground">Session</td>
                            </tr>
                            <tr>
                              <td className="border border-border p-3 text-muted-foreground">UserWay</td>
                              <td className="border border-border p-3 text-muted-foreground">UserWay Ltd.</td>
                              <td className="border border-border p-3 text-muted-foreground">Accessibility widget that helps users adjust font sizes, contrast, and other display settings for better readability.</td>
                              <td className="border border-border p-3 text-muted-foreground">Session</td>
                            </tr>
                            <tr>
                              <td className="border border-border p-3 text-muted-foreground">Local storage (UI state)</td>
                              <td className="border border-border p-3 text-muted-foreground">SeniorThrive</td>
                              <td className="border border-border p-3 text-muted-foreground">Stores functional application state such as sidebar preferences, assessment progress, and community guideline acceptance. Not used for tracking.</td>
                              <td className="border border-border p-3 text-muted-foreground">Persistent</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="bg-card border border-border rounded-xl p-6">
                      <h3 className="text-xl font-bold text-foreground mb-2">Tier 3: Marketing and Analytics Cookies</h3>
                      <p className="text-muted-foreground mb-4">These cookies are used to measure how visitors use our website, which pages are most popular, and how advertising campaigns perform. They may also be used to show you relevant advertisements on other platforms. These cookies are optional and require your consent.</p>

                      <div className="overflow-x-auto">
                        <table className="w-full border-collapse border border-border">
                          <thead>
                            <tr className="bg-accent">
                              <th className="border border-border p-3 text-left font-bold">Cookie / Technology</th>
                              <th className="border border-border p-3 text-left font-bold">Provider</th>
                              <th className="border border-border p-3 text-left font-bold">Purpose</th>
                              <th className="border border-border p-3 text-left font-bold">Duration</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="border border-border p-3 text-muted-foreground">Google Analytics (_ga, _ga_*)</td>
                              <td className="border border-border p-3 text-muted-foreground">Google LLC</td>
                              <td className="border border-border p-3 text-muted-foreground">Measures website traffic, page views, and user behavior to help us understand how visitors use our site and improve the experience. Measurement ID: G-2PNZHTHLS9.</td>
                              <td className="border border-border p-3 text-muted-foreground">Up to 2 years</td>
                            </tr>
                            <tr>
                              <td className="border border-border p-3 text-muted-foreground">Google Tag Manager</td>
                              <td className="border border-border p-3 text-muted-foreground">Google LLC</td>
                              <td className="border border-border p-3 text-muted-foreground">Manages the loading of analytics and marketing tags. Does not set cookies itself but controls which other tags fire based on your consent preferences.</td>
                              <td className="border border-border p-3 text-muted-foreground">Session</td>
                            </tr>
                            <tr>
                              <td className="border border-border p-3 text-muted-foreground">PostHog (ph_*, distinct_id)</td>
                              <td className="border border-border p-3 text-muted-foreground">PostHog, Inc.</td>
                              <td className="border border-border p-3 text-muted-foreground">Product analytics within the SeniorThrive web application. Records page views, sign-in events, and sign-up events with a pseudonymous user ID. Session recording is disabled. Does not collect name, email, address, phone, health, role, or care-relationship data.</td>
                              <td className="border border-border p-3 text-muted-foreground">Up to 1 year</td>
                            </tr>
                            <tr>
                              <td className="border border-border p-3 text-muted-foreground">Reddit Pixel (_rdt_uuid)</td>
                              <td className="border border-border p-3 text-muted-foreground">Reddit, Inc.</td>
                              <td className="border border-border p-3 text-muted-foreground">Tracks conversions from Reddit advertising campaigns (page visits, form submissions). Used to measure ad effectiveness and build audiences for remarketing.</td>
                              <td className="border border-border p-3 text-muted-foreground">Up to 1 year</td>
                            </tr>
                            <tr>
                              <td className="border border-border p-3 text-muted-foreground">UTM parameters</td>
                              <td className="border border-border p-3 text-muted-foreground">SeniorThrive</td>
                              <td className="border border-border p-3 text-muted-foreground">Stored in sessionStorage to attribute your visit to the marketing campaign that brought you here (utm_source, utm_medium, utm_campaign, utm_content, utm_term). Cleared when you close your browser tab.</td>
                              <td className="border border-border p-3 text-muted-foreground">Session</td>
                            </tr>
                            <tr>
                              <td className="border border-border p-3 text-muted-foreground">Referral code</td>
                              <td className="border border-border p-3 text-muted-foreground">SeniorThrive</td>
                              <td className="border border-border p-3 text-muted-foreground">Stored in localStorage when you arrive via a referral link, so the referring user receives credit if you sign up.</td>
                              <td className="border border-border p-3 text-muted-foreground">30 days</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Cookies on the SeniorThrive Web Application</h2>
                  <p className="text-muted-foreground">
                    The SeniorThrive web application (accessed after you log in) uses a more limited set of cookies and tracking technologies than the marketing website. The app uses strictly necessary cookies (Supabase authentication), functional cookies (Intercom for support, Sentry for error monitoring), product analytics (PostHog, Tier 3, opt-in), and local storage for application state. The app does not use Google Analytics or advertising pixels.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Third-Party Cookies</h2>
                  <p className="text-muted-foreground mb-4">
                    Some cookies are placed by third-party services that appear on our pages. We do not control these third-party cookies. The third-party providers listed in the tables above have their own privacy policies governing how they use the data they collect:
                  </p>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2">
                    <li><strong>Google:</strong> Google Privacy Policy (applies to Google Analytics, Tag Manager, and other Google services)</li>
                    <li><strong>Intercom:</strong> Intercom Privacy Policy</li>
                    <li><strong>Sentry:</strong> Sentry Privacy Policy</li>
                    <li><strong>Reddit:</strong> Reddit Privacy Policy</li>
                    <li><strong>UserWay:</strong> UserWay Privacy Policy</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Your Choices and Controls</h2>
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-bold text-foreground">Cookie Banner:</h3>
                      <p className="text-muted-foreground">When you first visit our website, you will see a cookie banner that lets you choose which tiers of cookies to accept: Necessary (always on), Functional, and Marketing. You can change your preferences at any time by clicking the cookie settings link in the footer of any page.</p>
                    </div>

                    <div>
                      <h3 className="text-lg font-bold text-foreground">Browser Settings:</h3>
                      <p className="text-muted-foreground">Most web browsers allow you to manage cookies through their settings. You can typically set your browser to refuse cookies, delete cookies, or alert you when a cookie is being set. The steps to do this vary by browser:</p>
                      <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2">
                        <li><strong>Chrome:</strong> Settings &gt; Privacy and security &gt; Cookies and other site data</li>
                        <li><strong>Safari:</strong> Preferences &gt; Privacy &gt; Manage Website Data</li>
                        <li><strong>Firefox:</strong> Settings &gt; Privacy &amp; Security &gt; Cookies and Site Data</li>
                        <li><strong>Edge:</strong> Settings &gt; Cookies and site permissions &gt; Manage and delete cookies</li>
                      </ul>
                      <p className="text-muted-foreground mt-2">Note that disabling cookies may affect the functionality of certain parts of the Services.</p>
                    </div>

                    <div>
                      <h3 className="text-lg font-bold text-foreground">Google Analytics Opt-Out:</h3>
                      <p className="text-muted-foreground">You can opt out of Google Analytics across all websites by installing the Google Analytics Opt-out Browser Add-on, available from Google.</p>
                    </div>

                    <div>
                      <h3 className="text-lg font-bold text-foreground">Do Not Track:</h3>
                      <p className="text-muted-foreground">Some browsers offer a "Do Not Track" (DNT) signal. There is currently no universal standard for how companies should respond to DNT signals. We recommend using our cookie banner and browser cookie settings for reliable control over tracking on our site.</p>
                    </div>

                    <div>
                      <h3 className="text-lg font-bold text-foreground">Global Privacy Control:</h3>
                      <p className="text-muted-foreground">We recognize and honor the Global Privacy Control (GPC) signal. If your browser sends a GPC signal, we will treat it as a valid opt-out of the sale or sharing of personal information under applicable state privacy laws, including the California Consumer Privacy Act (CCPA/CPRA).</p>
                    </div>
                  </div>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Changes to This Cookie Policy</h2>
                  <p className="text-muted-foreground">
                    We may update this Cookie Policy from time to time to reflect changes in the cookies we use or for other operational, legal, or regulatory reasons. We will post the updated policy on this page and update the "Last Updated" date. We encourage you to review this policy periodically.
                  </p>
                </section>

                <section>
                  <h2 className="text-2xl font-bold text-foreground mb-4">Contact Us</h2>
                  <p className="text-muted-foreground mb-4">
                    If you have questions about our use of cookies or this Cookie Policy, please contact us:
                  </p>
                  <div className="bg-card border border-border rounded-lg p-6">
                    <div className="text-muted-foreground space-y-2">
                      <p>
                        <strong>SeniorThrive LLC</strong><br />
                        5436 Via Carrizo, Laguna Woods, CA 92637<br />
                        Email: <a href="mailto:privacy@seniorthrive.com" className="text-primary hover:underline">privacy@seniorthrive.com</a>
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookiePolicy;
